import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { EXPENSE_CATEGORY_TOKENS, expenseCategoryLabel, type ExpenseCategory } from '@/lib/bills-display';
import { digitsOnly, numericInputProps } from '@/components/editorial';
import { formatMoney } from '@/i18n/intl';

// #641: single source of truth — the non-medical creation vocabulary lives in
// EXPENSE_CATEGORY_TOKENS (lib/bills-display.ts), lockstep with the backend enum.
const CATEGORIES = Object.keys(EXPENSE_CATEGORY_TOKENS) as ExpenseCategory[];

interface AttachReceiptDialogProps {
  /** The receipt the user just picked. Dialog is open while non-null. */
  file: File | null;
  /**
   * useChat.attachReceipt — resolves true on success, false on failure
   * (failure already surfaced a destructive toast; the form stays open
   * with its values intact so the user can retry).
   */
  onSubmit: (file: File, category: ExpenseCategory, amount: number) => Promise<boolean>;
  onClose: () => void;
}

/**
 * Mini-form shown after the chat paperclip's file pick (#641 — replaces the
 * retired bill-analysis attach). Asks category + amount, then does ONE
 * synchronous POST /api/documents/upload via useChat.attachReceipt.
 */
/**
 * #2109 R3 L5b: the receipt copy is money copy, so it lives in the `money`
 * namespace (lane L4's). The keys arrive through the PM's hunk to
 * `money.json` (plan "Integration", collision set); Cancel is common's.
 */
export function AttachReceiptDialog({ file, onSubmit, onClose }: AttachReceiptDialogProps) {
  const { t } = useTranslation(['money', 'common']);
  const [category, setCategory] = useState<ExpenseCategory>('travel');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // Fresh form for each newly picked file
  useEffect(() => {
    if (file) {
      setCategory('travel');
      setAmount('');
      setBusy(false);
      setValidationError(null);
      setSubmitted(false);
    }
  }, [file]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file || busy) return;
    setValidationError(null);
    const parsed = Number.parseFloat(amount);
    if (!amount || !Number.isFinite(parsed) || parsed <= 0) {
      setValidationError(t('money:chatReceipt.amountError', { min: formatMoney(0) }));
      return;
    }
    setBusy(true);
    const ok = await onSubmit(file, category, parsed);
    setBusy(false);
    if (ok) setSubmitted(true);
  }

  return (
    <Dialog
      open={!!file}
      onOpenChange={(open) => {
        if (!open && !busy) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md" data-testid="attach-receipt-dialog">
        <DialogHeader>
          <DialogTitle>{t('money:chatReceipt.title')}</DialogTitle>
          <DialogDescription className="truncate">
            {file?.name}
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <>
            {/* #2379: name the page by its nav label (Grants, not the
                retired "Finances") and offer the way there. */}
            <p className="text-body" data-testid="attach-receipt-success">
              {t('money:chatReceipt.submitted')}
            </p>
            <DialogFooter>
              <Button variant="ghost" asChild>
                <Link to="/bills" onClick={onClose}>{t('money:chatReceipt.openGrants')}</Link>
              </Button>
              <Button type="button" onClick={onClose} variant="brand">
                {t('money:chatReceipt.done')}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <select
                data-testid="attach-receipt-category"
                aria-label={t('money:chatReceipt.categoryLabel')}
                value={category}
                onChange={(e) => setCategory(e.target.value as ExpenseCategory)}
                disabled={busy}
                className="rounded-md border border-border/60 bg-background px-2.5 py-1.5 text-body"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{expenseCategoryLabel(c, t)}</option>
                ))}
              </select>
              <input
                data-testid="attach-receipt-amount"
                aria-label={t('money:chatReceipt.amountLabel')}
                {...numericInputProps({ decimal: true, max: 99999 })}
                placeholder={t('money:chatReceipt.amountPlaceholder')}
                value={amount}
                onChange={(e) => setAmount(digitsOnly(e.target.value, { decimal: true }))}
                disabled={busy}
                className="w-28 rounded-md border border-border/60 bg-background px-2.5 py-1.5 text-body"
              />
            </div>
            {validationError && <p className="text-body text-status-danger-fg">{validationError}</p>}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
                {t('common:actions.cancel')}
              </Button>
              <Button
                data-testid="attach-receipt-submit"
                type="submit"
                disabled={busy}
                variant="brand"
              >
                {busy ? t('money:chatReceipt.submitting') : t('money:chatReceipt.submit')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
